import React from 'react';
import { Link } from 'react-router-dom';
import { BlogPost } from '../types/blog';

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
  priority?: boolean;
}

const BlogCard: React.FC<BlogCardProps> = ({ post, featured = false }) => {
  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'ai': 'blue',
      'data-engineering': 'purple',
      'career': 'green',
      'tutorials': 'orange'
    };
    return colors[category] || 'gray';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <article className={`glass-card overflow-hidden hover:bg-white/10 transition-all duration-300 transform hover:scale-105 group ${
      featured ? 'md:col-span-1' : ''
    }`}>
      <Link to={`/blog/${post.slug}`}>
        {/* Featured Image */}
        <div className="relative overflow-hidden">
          <div className={`${featured ? 'h-64' : 'h-48'} bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-center`}>
            {/* Placeholder image - replace with actual images */}
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl">
                  {post.category === 'ai' ? '🤖' : 
                   post.category === 'data-engineering' ? '⚙️' : 
                   post.category === 'career' ? '🚀' : '📚'}
                </span>
              </div>
              <p className="text-gray-400 text-sm">Featured Image</p>
            </div>
          </div>
          
          {/* Category Badge */}
          <div className="absolute top-4 left-4">
            <span className={`px-3 py-1 bg-${getCategoryColor(post.category)}-500/20 text-${getCategoryColor(post.category)}-300 text-xs font-medium rounded-full border border-${getCategoryColor(post.category)}-500/30`}>
              {post.category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </span>
          </div>

          {/* Featured Badge */}
          {featured && (
            <div className="absolute top-4 right-4">
              <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-medium rounded-full border border-yellow-500/30">
                ⭐ Featured
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Meta Information */}
          <div className="flex items-center justify-between text-sm text-gray-400 mb-3">
            <div className="flex items-center space-x-4">
              <span>{formatDate(post.publishedDate)}</span>
              <span>•</span>
              <span>{post.readingTime} min read</span>
            </div>
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L10 9.586V6z" clipRule="evenodd" />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h3 className={`font-bold text-white mb-3 group-hover:text-blue-400 transition-colors line-clamp-2 ${
            featured ? 'text-xl md:text-2xl' : 'text-lg'
          }`}>
            {post.title}
          </h3>

          {/* Excerpt */}
          <p className={`text-gray-400 mb-4 line-clamp-3 ${
            featured ? 'text-base' : 'text-sm'
          }`}>
            {post.excerpt}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-700/50 text-gray-300 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
            {post.tags.length > 3 && (
              <span className="px-2 py-1 bg-gray-700/50 text-gray-300 text-xs rounded-full">
                +{post.tags.length - 3}
              </span>
            )}
          </div>

          {/* Author */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {post.author.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div>
                <p className="text-white text-sm font-medium">{post.author.name}</p>
              </div>
            </div>
            
            {/* Read More Arrow */}
            <div className="text-blue-400 group-hover:text-blue-300 transition-colors">
              <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default BlogCard;